'use client';

export interface Config3DMessage {
  source: 'config3d-player' | 'config3d-sdk';
  type: string;
  payload: unknown;
  requestId?: string;
}

export interface EmbedSnapshotRequest {
  width?: number;
  height?: number;
  mimeType?: 'image/png' | 'image/jpeg';
  quality?: number;
  fit?: 'contain' | 'cover';
}

export interface EmbedSnapshotResponse {
  dataUrl: string;
  width: number;
  height: number;
  pixelRatio: number;
  upscaled: boolean;
}

export type EmbedActionHandler = {
  setAttribute: (attributeId: string, value: string | number | boolean) => void;
  selectComponent: (componentId: string, variantId: string) => void;
  resetConfiguration: () => void;
  getState: () => unknown;
  requestSnapshot?: (options: EmbedSnapshotRequest) => Promise<EmbedSnapshotResponse | null>;
};

function parseSnapshotRequest(payload: unknown): EmbedSnapshotRequest {
  const p = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>;
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : undefined);
  return {
    width: num(p.width),
    height: num(p.height),
    mimeType: p.mimeType === 'image/jpeg' ? 'image/jpeg' : 'image/png',
    quality: typeof p.quality === 'number' ? p.quality : undefined,
    fit: p.fit === 'cover' ? 'cover' : 'contain',
  };
}

function isConfig3DMessage(data: unknown): data is Config3DMessage {
  if (typeof data !== 'object' || data === null) return false;
  const msg = data as Record<string, unknown>;
  return msg.source === 'config3d-sdk' && typeof msg.type === 'string';
}

export class EmbedBridge {
  private handler: EmbedActionHandler | null = null;

  constructor() {
    this.onMessage = this.onMessage.bind(this);
    window.addEventListener('message', this.onMessage);
  }

  setHandler(handler: EmbedActionHandler | null) {
    this.handler = handler;
  }

  private onMessage(event: MessageEvent) {
    if (!this.handler) return;
    if (!isConfig3DMessage(event.data)) return;

    const { type, payload, requestId } = event.data;

    switch (type) {
      case 'c3d:action': {
        const p = payload as Record<string, unknown>;
        if (p.action === 'setAttribute' && typeof p.attributeId === 'string') {
          this.handler.setAttribute(p.attributeId, p.value as string);
        } else if (p.action === 'selectComponent' && typeof p.componentId === 'string') {
          this.handler.selectComponent(p.componentId, p.variantId as string);
        } else if (p.action === 'resetConfiguration') {
          this.handler.resetConfiguration();
        }
        break;
      }
      case 'c3d:getState': {
        const state = this.handler.getState();
        this.emit('c3d:state', state, requestId);
        break;
      }
      case 'c3d:requestSnapshot': {
        const capture = this.handler.requestSnapshot;
        if (!capture) {
          this.emit('c3d:snapshot', { error: 'Snapshots are not available on this player' }, requestId);
          break;
        }
        const request = parseSnapshotRequest(payload);
        capture(request)
          .then((result) => {
            if (!result) {
              this.emit('c3d:snapshot', { error: 'Snapshot could not be captured' }, requestId);
              return;
            }
            const message = {
              /** Ready to drop straight into an `img` src. */
              url: result.dataUrl,
              dataUrl: result.dataUrl,
              mimeType: request.mimeType ?? 'image/png',
              width: result.width,
              height: result.height,
              pixelRatio: result.pixelRatio,
              upscaled: result.upscaled,
            };
            this.emit('c3d:snapshot', message, requestId);
            // Broadcast as well, so a host UI can render the image from an event
            // without having to hold on to the request promise.
            this.emit('c3d:snapshot.ready', { ...message, requestId });
          })
          .catch((err: unknown) => {
            const message = err instanceof Error ? err.message : 'Snapshot failed';
            this.emit('c3d:snapshot', { error: message }, requestId);
          });
        break;
      }
    }
  }

  emit(type: string, payload?: unknown, requestId?: string) {
    const msg: Config3DMessage = {
      source: 'config3d-player',
      type,
      payload: payload ?? null,
      requestId,
    };
    if (window.parent !== window) {
      window.parent.postMessage(msg, '*');
      return;
    }
    /** Same-document embed (`embed.js` + `inline: true`): SDK listens on `window` like the iframe path. */
    window.postMessage(msg, '*');
  }

  destroy() {
    window.removeEventListener('message', this.onMessage);
    this.handler = null;
  }
}

let bridgeInstance: EmbedBridge | null = null;

export function getEmbedBridge(): EmbedBridge {
  if (!bridgeInstance) {
    bridgeInstance = new EmbedBridge();
  }
  return bridgeInstance;
}
